import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from './useAuth';
import api, { API_BASE_URL } from '../services/api';
import { mintDocumentViewUrl } from '../services/documentViewUrl';
import { useToast } from '../components/ToastProvider';
import { derivativesService, type FunctionPromptMapping } from '../services/derivativesService';
import type { KBDocumentDetails } from '../services/knowledgeBaseService';
import { useOriginalFetchJobs } from './useOriginalFetchJobs';

export interface DocumentDerivative {
  id: string;
  derivative_type: string;
  file_path: string;
  file_size: number;
  created_date: string;
  metadata?: Record<string, unknown>;
}

export interface DerivativeFunction {
  value: string;
  label: string;
}

export interface MarkdownViewerState {
  title: string;
  content: string;
}

/**
 * Shared derivatives logic for the KB documents dropdown. Owns the per-document
 * derivative cache, the processing flag, the dynamic-function registry and the
 * markdown viewer state. One instance per documents table; rows render via the
 * presentational <DerivativesDropdown> component.
 */
export function useDocumentDerivatives(kbId: string | undefined) {
  const { t, i18n } = useTranslation();
  const { user } = useAuth();
  const { addToast } = useToast();
  const [derivatives, setDerivatives] = useState<Record<string, DocumentDerivative[]>>({});
  const [processingDoc, setProcessingDoc] = useState<string | null>(null);
  const [dynamicFunctions, setDynamicFunctions] = useState<Record<string, FunctionPromptMapping>>({});
  const [markdownViewer, setMarkdownViewer] = useState<MarkdownViewerState | null>(null);
  const { fetchJobs, startFetch, cancelFetch } = useOriginalFetchJobs(kbId);

  useEffect(() => {
    derivativesService.getFunctionPromptsMapping()
      .then(mappings => setDynamicFunctions(mappings))
      .catch(err => console.error('Error loading derivative functions:', err));
  }, []);

  const availableFunctions = useMemo<DerivativeFunction[]>(() => {
    const isSysAdmin = ['SysAdmin', 'DevOps'].includes(user?.role || '');
    const lang = i18n.language.startsWith('fr') ? 'fr' : 'en';
    const hardcoded = [
      { value: 'ORIGINAL', label: t('kbDocuments.functions.original') },
      { value: 'TEXT', label: t('kbDocuments.functions.text') },
      { value: 'BIBLIO', label: t('kbDocuments.functions.biblio') },
      { value: 'FAMILY', label: t('kbDocuments.functions.family') },
    ];
    const dynamic = Object.entries(dynamicFunctions).map(([name, config]) => ({
      value: name,
      label: lang === 'fr' ? (config.label_fr || name) : (config.label_en || name),
    }));
    const admin = isSysAdmin ? [{ value: 'CHUNKS', label: t('kbDocuments.functions.chunks') }] : [];
    return [...hardcoded, ...dynamic, ...admin];
  }, [dynamicFunctions, user, i18n.language, t]);

  const getFunctionLabel = useCallback(
    (functionName: string): string => {
      const lang = i18n.language.startsWith('fr') ? 'fr' : 'en';
      switch (functionName) {
        case 'ORIGINAL': return t('kbDocuments.functions.original');
        case 'TEXT': return t('kbDocuments.functions.text');
        case 'BIBLIO': return t('kbDocuments.functions.biblio');
        case 'FAMILY': return t('kbDocuments.functions.family');
        case 'CHUNKS': return t('kbDocuments.functions.chunks');
        default: {
          const config = dynamicFunctions[functionName];
          if (config) return lang === 'fr' ? (config.label_fr || functionName) : (config.label_en || functionName);
          return functionName;
        }
      }
    },
    [t, i18n.language, dynamicFunctions]
  );

  const loadDocumentDerivatives = useCallback(
    async (documentId: string): Promise<DocumentDerivative[]> => {
      if (!kbId) return [];
      try {
        const response = await api.get(`/knowledge-bases/${kbId}/documents/${documentId}/derivatives`);
        const list = response.data.derivatives || [];
        setDerivatives(prev => ({ ...prev, [documentId]: list }));
        return list;
      } catch (error) {
        console.error('Error loading derivatives:', error);
        return [];
      }
    },
    [kbId]
  );

  const hasDerivative = useCallback(
    (documentId: string, derivativeType: string): boolean => {
      if (['ORIGINAL', 'BIBLIO', 'CHUNKS'].includes(derivativeType)) return false;
      const docDerivatives = derivatives[documentId] || [];
      const targetType = derivativeType === 'TEXT' ? 'Markdown' : derivativeType;
      return docDerivatives.some(d => d.derivative_type === targetType);
    },
    [derivatives]
  );

  // Open a browser document-view in a new tab/window WITHOUT putting the long-lived
  // access token in the URL. We mint a short-lived (~60s), single-document token and
  // use that instead. The blank window is opened synchronously so the browser keeps the
  // click gesture (avoids pop-up blockers) while the token request is in flight.
  const openDocumentView = useCallback(
    async (documentId: string, pathAndQuery: string, features?: string) => {
      if (!kbId) return;
      const win = window.open('', '_blank', features);
      if (!win) {
        addToast({ type: 'error', title: t('kbDocuments.viewTitle'), message: t('kbDocuments.popupBlocked') });
        return;
      }
      try {
        const { data } = await api.post(`/knowledge-bases/${kbId}/documents/${documentId}/view-token`);
        const sep = pathAndQuery.includes('?') ? '&' : '?';
        win.location.href = `${API_BASE_URL}/knowledge-bases/${kbId}/documents/${documentId}/${pathAndQuery}${sep}token=${data.token}`;
      } catch {
        win.close();
        addToast({ type: 'error', title: t('kbDocuments.viewTitle'), message: t('kbDocuments.viewFailed') });
      }
    },
    [kbId, addToast, t]
  );

  const executeFunction = useCallback(
    async (doc: KBDocumentDetails, functionName: string) => {
      if (!functionName || !kbId) return;
      const documentId = doc.id;
      setProcessingDoc(documentId);
      const functionLabel = getFunctionLabel(functionName);
      const lang = i18n.language.startsWith('fr') ? 'fr' : 'en';
      try {
        if (functionName === 'ORIGINAL') {
          // Scoped short-lived view token (never the session JWT in a URL)
          const pdfUrl = await mintDocumentViewUrl(kbId, documentId);
          let pdfExists = false;
          try {
            const checkResponse = await fetch(pdfUrl, { method: 'HEAD' });
            pdfExists = checkResponse.ok;
          } catch {
            pdfExists = false;
          }
          if (pdfExists) {
            window.open(pdfUrl, '_blank');
            return;
          }
          // No stored PDF — start a background fetch job (EPO OPS for patents: OPS holds
          // images for EP, WO, US and many other authorities; open-access cascade for
          // scientific publications). Progress shows in the row; a toast with an "open"
          // link fires on completion (useOriginalFetchJobs).
          const fetchable =
            (doc.document_type === 'patent' && doc.patent_id) || doc.document_type === 'scientific_publication';
          if (!fetchable) {
            addToast({ type: 'info', title: t('kbDocuments.functions.original'), message: t('kbDocuments.originalNotAvailable') });
            return;
          }
          if (fetchJobs[documentId]) return; // already retrieving — the row shows progress
          try {
            const outcome = await startFetch(documentId);
            if (outcome === 'already_stored') {
              window.open(pdfUrl, '_blank');
            } else {
              addToast({ type: 'info', title: t('kbDocuments.functions.original'), message: t('kbDocuments.fetchQueued') });
            }
          } catch {
            addToast({ type: 'error', title: t('kbDocuments.functions.original'), message: t('kbDocuments.fetchFailed') });
          }
          return;
        }

        if (functionName === 'BIBLIO') {
          await openDocumentView(documentId, `biblio/html?lang=${lang}`, 'width=900,height=700,scrollbars=yes,resizable=yes');
          return;
        }

        if (functionName === 'CHUNKS') {
          await openDocumentView(documentId, 'chunks/html');
          return;
        }

        if (functionName === 'FAMILY') {
          if (doc.document_type !== 'patent') {
            addToast({ type: 'error', title: t('kbDocuments.functions.family'), message: t('kbDocuments.familyPatentOnly') });
            return;
          }
          const response = await api.get(`/knowledge-bases/${kbId}/documents/${documentId}/family`);
          const members: string[] = response.data.family_members || [];
          const rootId: string = response.data.patent_id;
          const grouped: Record<string, string[]> = {};
          for (const pub of members) {
            const cc = pub.length >= 2 ? pub.slice(0, 2) : '??';
            if (!grouped[cc]) grouped[cc] = [];
            grouped[cc].push(pub);
          }
          const lines: string[] = [`## ${t('kbDocuments.familyTitle', { patent: rootId })}`, `**${t('kbDocuments.familyCount', { count: members.length })}**`, ''];
          for (const [cc, pubs] of Object.entries(grouped).sort()) {
            lines.push(`**${cc}**`);
            const byBase: Record<string, string[]> = {};
            for (const pub of pubs) {
              const base = pub.replace(/[A-Z]\d*$/, '');
              if (!byBase[base]) byBase[base] = [];
              byBase[base].push(pub);
            }
            for (const variants of Object.values(byBase)) {
              lines.push(variants.join(' · '));
            }
            lines.push('');
          }
          setMarkdownViewer({ title: t('kbDocuments.functions.family'), content: lines.join('\n') });
          return;
        }

        if (functionName === 'TEXT') {
          if (doc.document_type === 'patent') {
            await openDocumentView(documentId, `patent-text/html?lang=${lang}`, 'width=1000,height=800,scrollbars=yes,resizable=yes');
          } else {
            const docDerivatives = derivatives[documentId] || (await loadDocumentDerivatives(documentId));
            const markdownDerivative = docDerivatives.find(d => d.derivative_type === 'Markdown');
            if (markdownDerivative) {
              await openDocumentView(documentId, 'ocr/html', 'width=1000,height=800,scrollbars=yes,resizable=yes');
            } else {
              addToast({ type: 'info', title: t('kbDocuments.processingTitle'), message: t('kbDocuments.generatingOCR') });
              const response = await api.post(`/knowledge-bases/${kbId}/documents/${documentId}/ocr`);
              if (response.data.success) {
                addToast({ type: 'success', title: t('kbDocuments.generatedTitle'), message: t('kbDocuments.ocrComplete') });
                await loadDocumentDerivatives(documentId);
                await openDocumentView(documentId, 'ocr/html', 'width=1000,height=800,scrollbars=yes,resizable=yes');
              } else {
                addToast({ type: 'error', title: t('kbDocuments.generationFailedTitle'), message: response.data.message || t('kbDocuments.ocrFailed') });
              }
            }
          }
          return;
        }

        // Dynamic function
        const docDerivatives = derivatives[documentId] || (await loadDocumentDerivatives(documentId));
        const existingDerivative = docDerivatives.find(d => d.derivative_type === functionName);

        const openDerivativeViewer = async (derivativeId: string) => {
          const response = await api.get(`/knowledge-bases/${kbId}/documents/${documentId}/derivatives/${derivativeId}/view`);
          const content = response.data.content || t('kbDocuments.noContent');
          setMarkdownViewer({ title: functionLabel, content });
        };

        if (existingDerivative) {
          await openDerivativeViewer(existingDerivative.id);
        } else {
          addToast({ type: 'info', title: t('kbDocuments.processingTitle'), message: t('kbDocuments.generating', { name: functionLabel }) });
          const genResponse = functionName === 'Summary'
            ? await api.post(`/knowledge-bases/${kbId}/documents/${documentId}/summary`)
            : await api.post(`/knowledge-bases/${kbId}/documents/${documentId}/process/${functionName}`);
          if (genResponse.data.success) {
            addToast({ type: 'success', title: t('kbDocuments.generatedTitle'), message: t('kbDocuments.generatedMessage', { name: functionLabel }) });
            const updatedDerivatives = await loadDocumentDerivatives(documentId);
            const newDerivative = updatedDerivatives.find(d => d.derivative_type === functionName);
            if (newDerivative) await openDerivativeViewer(newDerivative.id);
          } else {
            addToast({ type: 'error', title: t('kbDocuments.generationFailedTitle'), message: genResponse.data.message || t('kbDocuments.generationFailedMessage', { name: functionLabel }) });
          }
        }
      } catch (error: unknown) {
        console.error(`Error executing ${functionName}:`, error);
        addToast({ type: 'error', title: t('kbDocuments.errorTitle'), message: (error as { response?: { data?: { detail?: string } } })?.response?.data?.detail || t('kbDocuments.errorMessage', { name: functionLabel }) });
      } finally {
        setProcessingDoc(null);
      }
    },
    [kbId, getFunctionLabel, i18n.language, t, addToast, derivatives, loadDocumentDerivatives, openDocumentView, fetchJobs, startFetch]
  );

  return {
    derivatives,
    availableFunctions,
    processingDoc,
    markdownViewer,
    setMarkdownViewer,
    loadDocumentDerivatives,
    hasDerivative,
    executeFunction,
    fetchJobs,
    cancelFetch,
  };
}
